import type { CanonicalCut } from '@/core/cuts'

export type CutFlowPhase = 'idle' | 'list' | 'preview' | 'muAdjust' | 'confirm' | 'applying' | 'success' | 'error'

export interface CutFlowState {
  phase: CutFlowPhase
  availableCuts: CanonicalCut[]
  cutsHistoryCount: number
  selectedCutIndex: number | null
  selectedCut: CanonicalCut | null
  muValue: number | null
  lastError: string | null
}

export type CutFlowEvent =
  | { type: 'CUT_LIST_REFRESHED'; cuts: CanonicalCut[] }
  | { type: 'CUT_SELECTED'; cutIndex: number; cut: CanonicalCut }
  | { type: 'CUT_PREVIEW_READY' }
  | { type: 'CUT_MU_ADJUST_REQUIRED'; mu: number }
  | { type: 'MU_CHANGED'; mu: number }
  | { type: 'CUT_CONFIRMED' }
  | { type: 'CUT_CANCELLED' }
  | { type: 'CUT_APPLY_SUCCEEDED' }
  | { type: 'CUT_APPLY_FAILED'; error: string }

export const initialCutFlowState = (): CutFlowState => ({
  phase: 'idle',
  availableCuts: [],
  cutsHistoryCount: 0,
  selectedCutIndex: null,
  selectedCut: null,
  muValue: null,
  lastError: null,
})

export function reduceCutFlow(state: CutFlowState, event: CutFlowEvent): CutFlowState {
  switch (event.type) {
    case 'CUT_LIST_REFRESHED': {
      const nextPhase = event.cuts.length > 0 ? 'list' : 'idle'
      return {
        ...state,
        phase: nextPhase,
        availableCuts: event.cuts,
        selectedCutIndex: null,
        selectedCut: null,
        muValue: null,
        lastError: null,
      }
    }
    case 'CUT_SELECTED':
      return {
        ...state,
        phase: 'preview',
        selectedCutIndex: event.cutIndex,
        selectedCut: event.cut,
        muValue: null,
        lastError: null,
      }
    case 'CUT_PREVIEW_READY':
      return {
        ...state,
        phase: 'confirm',
        lastError: null,
      }
    case 'CUT_MU_ADJUST_REQUIRED':
      return {
        ...state,
        phase: 'muAdjust',
        muValue: event.mu,
        lastError: null,
      }
    case 'MU_CHANGED':
      return {
        ...state,
        phase: 'muAdjust',
        muValue: event.mu,
      }
    case 'CUT_CONFIRMED':
      return {
        ...state,
        phase: 'applying',
        lastError: null,
      }
    case 'CUT_CANCELLED': {
      const nextPhase = state.availableCuts.length > 0 ? 'list' : 'idle'
      return {
        ...state,
        phase: nextPhase,
        selectedCutIndex: null,
        selectedCut: null,
        muValue: null,
        lastError: null,
      }
    }
    case 'CUT_APPLY_SUCCEEDED':
      return {
        ...state,
        phase: 'success',
        lastError: null,
      }
    case 'CUT_APPLY_FAILED':
      return {
        ...state,
        phase: 'error',
        lastError: event.error,
      }
    default:
      return state
  }
}

export type BnBFlowPhase = 'idle' | 'active' | 'selecting' | 'branching' | 'backtracking' | 'finished'

export type BnBFlowEvent =
  | { type: 'BNB_START_REQUESTED' }
  | { type: 'BNB_NODE_SELECTED' }
  | { type: 'BNB_BRANCH_REQUESTED' }
  | { type: 'BNB_BACKTRACK_REQUESTED' }
  | { type: 'BNB_COMMAND_COMPLETED' }
  | { type: 'BNB_LIMIT_CHANGED' }
  | { type: 'BNB_RESET_REQUESTED' }
  | { type: 'BNB_STARTED' }
  | { type: 'BNB_FINISHED' }

export function reduceBnBFlow(phase: BnBFlowPhase, event: BnBFlowEvent): BnBFlowPhase {
  switch (event.type) {
    case 'BNB_START_REQUESTED':
      return phase === 'active' ? 'active' : 'active'
    case 'BNB_NODE_SELECTED':
      return phase === 'active' ? 'selecting' : phase
    case 'BNB_BRANCH_REQUESTED':
      return phase === 'active' || phase === 'selecting' ? 'branching' : phase
    case 'BNB_BACKTRACK_REQUESTED':
      return phase === 'active' || phase === 'selecting' ? 'backtracking' : phase
    case 'BNB_COMMAND_COMPLETED':
      return phase === 'idle' || phase === 'finished' ? phase : 'active'
    case 'BNB_LIMIT_CHANGED':
      return phase === 'idle' ? 'idle' : 'active'
    case 'BNB_RESET_REQUESTED':
      return 'idle'
    case 'BNB_STARTED':
      return 'active'
    case 'BNB_FINISHED':
      return 'finished'
    default:
      return phase
  }
}
